import { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { PublicHeader } from '@/components/PublicHeader';
import { SeoHead } from '@/components/SeoHead';

export default function Auth() {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(() => {
    const mode = searchParams.get('mode');
    return mode !== 'signup';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailExistsError, setEmailExistsError] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const emailSchema = z.string().email(t('authInvalidCredentials'));
  const passwordSchema = z.string().min(6, t('authPasswordNotMeet'));

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  // Check if user exists but email is not confirmed
  if (user && !user.email_confirmed_at) {
    return (
      <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">{t('confirmEmailTitle')}</CardTitle>
            <CardDescription className="mt-2">
              {t('confirmEmailSent')} <span className="font-medium text-foreground">{user.email}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              {t('confirmEmailInstruction')}
            </p>
            <div className="pt-4 border-t border-border">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                }}
              >
                {t('backToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
        <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
          {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
        </p>
      </div>
    );
  }

  // User is confirmed, redirect to main app
  if (user && user.email_confirmed_at) {
    return <Navigate to="/" replace />;
  }

  // Show pending confirmation screen after signup
  if (pendingConfirmation) {
    return (
      <>
        <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-xl">{t('confirmEmailTitle')}</CardTitle>
              <CardDescription className="mt-2">
                {t('confirmEmailSent')} <span className="font-medium text-foreground">{pendingEmail}</span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('confirmEmailRegistrationPart1')}{' '}
                <button
                  type="button"
                  onClick={() => setConsentModalOpen(true)}
                  className="text-primary underline hover:text-primary/80 transition-colors"
                >
                  {t('privacyPolicyLabel')}
                </button>
                {t('confirmEmailRegistrationPart2')}
              </p>
              <div className="pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setPendingConfirmation(false);
                    setPendingEmail('');
                    setIsLogin(true);
                  }}
                >
                  {t('backToLogin')}
                </Button>
              </div>
            </CardContent>
          </Card>
          <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
            {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>

        {/* Personal Data Processing Consent Modal */}
        <Dialog open={consentModalOpen} onOpenChange={setConsentModalOpen}>
          <DialogContent className="max-w-full h-full sm:max-w-full sm:h-full sm:rounded-none p-0 gap-0">
            <DialogHeader className="p-4 border-b sticky top-0 bg-background z-10">
              <div className="flex items-center justify-between">
                <DialogTitle>{t('consentModalTitle')}</DialogTitle>
                <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </DialogClose>
              </div>
            </DialogHeader>
            <ScrollArea className="flex-1 h-[calc(100dvh-80px)]">
              <div className="p-6 space-y-4 text-sm text-muted-foreground">
                <h2 className="text-lg font-semibold text-foreground">{t('consentTitle')}</h2>
                
                <p>{t('consentIntro')}</p>
                
                <h3 className="text-base font-medium text-foreground mt-6">{t('consentOperator')}</h3>
                <p>{t('consentOperatorIntro')}</p>
                <div className="mt-2 space-y-1">
                  <p><span className="font-medium text-foreground">{t('consentCompanyName')}</span> tugedr s.r.o.</p>
                  <p><span className="font-medium text-foreground">{t('consentAddress')}</span> Súkennícka 19058/1 Bratislava - mestská časť Ružinov 821 09</p>
                  <p><span className="font-medium text-foreground">{t('consentRegNumber')}</span> 52 813 282</p>
                  <p><span className="font-medium text-foreground">{t('consentEmail')}</span> peter@tugedr.com</p>
                </div>
                
                <h3 className="text-base font-medium text-foreground mt-6">{t('consentScope')}</h3>
                <p>{t('consentScopeIntro')}</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>{t('consentScopeEmail')}</li>
                  <li>{t('consentScopeNickname')}</li>
                  <li>{t('consentScopeUsage')}</li>
                </ul>
                
                <h3 className="text-base font-medium text-foreground mt-6">{t('consentPurpose')}</h3>
                <p>{t('consentPurposeIntro')}</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>{t('consentPurposeAccount')}</li>
                  <li>{t('consentPurposeApp')}</li>
                  <li>{t('consentPurposeImprove')}</li>
                </ul>
                
                <h3 className="text-base font-medium text-foreground mt-6">{t('consentDuration')}</h3>
                <p>{t('consentDurationIntro')}</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>{t('consentDurationAccount')}</li>
                  <li>{t('consentDurationRevoke')}</li>
                </ul>
                
                <h3 className="text-base font-medium text-foreground mt-6">{t('consentRights')}</h3>
                <p>{t('consentRightsIntro')}</p>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>{t('consentRightsAccess')}</li>
                  <li>{t('consentRightsCorrect')}</li>
                  <li>{t('consentRightsRestrict')}</li>
                  <li>{t('consentRightsPortability')}</li>
                  <li>{t('consentRightsComplaint')}</li>
                </ul>
                
                <p className="mt-6">{t('consentConclusion')}</p>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Check if password meets the displayed requirements (8+ chars, lowercase, uppercase, numbers)
  const passwordMeetsRequirements = password.length >= 8 && 
    /[a-z]/.test(password) && 
    /[A-Z]/.test(password) && 
    /[0-9]/.test(password);

  // For signup, require consent checkbox to be checked
  const canSubmit = isLogin || consentChecked;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous email exists error
    setEmailExistsError(false);
    
    // Block signup if consent not given
    if (!isLogin && !consentChecked) {
      toast.error(t('authConsentRequired'));
      return;
    }
    
    try {
      emailSchema.parse(email);
      // Only validate password schema on signup
      if (!isLogin) {
        passwordSchema.parse(password);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        // Only show toast for email validation errors, not password (handled via helper text)
        const emailError = err.errors.find(e => e.path.includes('email') || e.message.includes('email'));
        if (emailError) {
          toast.error(emailError.message);
        }
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast.error(t('authInvalidCredentials'));
          } else if (error.message.includes('Email not confirmed')) {
            // User exists but hasn't confirmed email
            setPendingEmail(email);
            setPendingConfirmation(true);
          } else {
            // Only log errors in development mode
            if (import.meta.env.DEV) {
              console.error('Login error:', error.message);
            }
            toast.error(t('authUnableToSignIn'));
          }
        }
      } else {
        const { error, data } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        if (error) {
          if (error.message.includes('already registered')) {
            // Show inline error with CTA instead of toast
            setEmailExistsError(true);
          } else if (error.message.includes('Password')) {
            toast.error(t('authPasswordNotMeet'));
          } else {
            // Only log errors in development mode
            if (import.meta.env.DEV) {
              console.error('Signup error:', error.message);
            }
            toast.error(t('authUnableToCreate'));
          }
        } else if (data.user && !data.user.email_confirmed_at) {
          // Show pending confirmation screen
          setPendingEmail(email);
          setPendingConfirmation(true);
        }
      }
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    try {
      emailSchema.parse(resetEmail);
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
        return;
      }
    }
    
    setIsResettingPassword(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      // Always show success message regardless of whether email exists (security)
      if (error) {
        // Only log in dev mode
        if (import.meta.env.DEV) {
          console.error('Password reset error:', error.message);
        }
      }
      
      // Always show success state
      setResetEmailSent(true);
    } catch {
      // Still show success for security (don't reveal if email exists)
      setResetEmailSent(true);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Forgot password - success state
  if (showForgotPassword && resetEmailSent) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4 pt-20">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-xl">{t('resetPasswordSuccessTitle')}</CardTitle>
              <CardDescription className="mt-2">
                {t('resetPasswordSuccessMessage')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmailSent(false);
                  setResetEmail('');
                }}
              >
                {t('resetPasswordBackToSignIn')}
              </Button>
            </CardContent>
          </Card>
          <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
            {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </>
    );
  }

  // Forgot password - request form
  if (showForgotPassword) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4 pt-20">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-serif">{t('resetPasswordTitle')}</CardTitle>
              <CardDescription>{t('resetPasswordSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">{t('resetPasswordEmailLabel')}</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder={t('authEmailPlaceholder')}
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? t('authPleaseWait') : t('resetPasswordSendLink')}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('resetPasswordBackToSignIn')}
                </button>
              </div>
            </CardContent>
          </Card>
          <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
            {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHead
        title="Sign in or create an account — Drinks in 365 Days"
        description="Sign in or create your account to join the 100 drinks in 365 days challenge, log every drink, and compete with your friends."
        path="/auth"
      />
      <PublicHeader />
      <div className="min-h-screen-safe relative flex flex-col items-center justify-center bg-background p-4 pt-20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif">{t('authTitle')}</CardTitle>
            <CardDescription>{t('authSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('authEmail')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('authEmailPlaceholder')}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('authPassword')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('authPasswordPlaceholder')}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {!isLogin && (
                  <p className={`text-xs ${password.length > 0 && !passwordMeetsRequirements ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {t('authPasswordRequirements')}
                  </p>
                )}
              </div>
              
              {/* Consent checkbox - only shown during signup */}
              {!isLogin && (
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked === true)}
                  />
                  <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
                    {t('authConsentLabel')}{' '}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setConsentModalOpen(true);
                      }}
                      className="text-primary underline hover:text-primary/80 transition-colors"
                    >
                      {t('authConsentLink')}
                    </button>
                  </Label>
                </div>
              )}
              
              {/* Email exists error - shown inline instead of toast */}
              {emailExistsError && !isLogin && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-2">
                  <p className="text-sm text-destructive font-medium">
                    {t('authEmailExistsMessage')}
                  </p>
                  <Button 
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setEmailExistsError(false);
                      setIsLogin(true);
                    }}
                  >
                    {t('authEmailExistsCta')}
                  </Button>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting || !canSubmit}
              >
                {isSubmitting ? t('authPleaseWait') : isLogin ? t('authSignIn') : t('authCreateAccount')}
              </Button>
            </form>
            
            {/* Forgot password link - only shown on login */}
            {isLogin && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('forgotPasswordLink')}
                </button>
              </div>
            )}
            
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setConsentChecked(false);
                  setEmailExistsError(false);
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isLogin ? t('authNoAccount') : t('authHaveAccount')}
              </button>
            </div>
          </CardContent>
        </Card>
        <p className="absolute bottom-4 text-center text-xs text-muted-foreground">
          {t('footerCopyright').replace('{year}', new Date().getFullYear().toString())}
        </p>
      </div>

      {/* Personal Data Processing Consent Modal */}
      <Dialog open={consentModalOpen} onOpenChange={setConsentModalOpen}>
        <DialogContent className="max-w-full h-full sm:max-w-full sm:h-full sm:rounded-none p-0 gap-0">
          <DialogHeader className="p-4 border-b sticky top-0 bg-background z-10">
            <div className="flex items-center justify-between">
              <DialogTitle>{t('consentModalTitle')}</DialogTitle>
              <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>
          <ScrollArea className="flex-1 h-[calc(100dvh-80px)]">
            <div className="p-6 space-y-4 text-sm text-muted-foreground">
              <h2 className="text-lg font-semibold text-foreground">{t('consentTitle')}</h2>
              
              <p>{t('consentIntro')}</p>
              
              <h3 className="text-base font-medium text-foreground mt-6">{t('consentOperator')}</h3>
              <p>{t('consentOperatorIntro')}</p>
              <div className="mt-2 space-y-1">
                <p><span className="font-medium text-foreground">{t('consentCompanyName')}</span> tugedr s.r.o.</p>
                <p><span className="font-medium text-foreground">{t('consentAddress')}</span> Súkennícka 19058/1 Bratislava - mestská časť Ružinov 821 09</p>
                <p><span className="font-medium text-foreground">{t('consentRegNumber')}</span> 52 813 282</p>
                <p><span className="font-medium text-foreground">{t('consentEmail')}</span> peter@tugedr.com</p>
              </div>
              
              <h3 className="text-base font-medium text-foreground mt-6">{t('consentScope')}</h3>
              <p>{t('consentScopeIntro')}</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>{t('consentScopeEmail')}</li>
                <li>{t('consentScopeNickname')}</li>
                <li>{t('consentScopeUsage')}</li>
              </ul>
              
              <h3 className="text-base font-medium text-foreground mt-6">{t('consentPurpose')}</h3>
              <p>{t('consentPurposeIntro')}</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>{t('consentPurposeAccount')}</li>
                <li>{t('consentPurposeApp')}</li>
                <li>{t('consentPurposeImprove')}</li>
              </ul>
              
              <h3 className="text-base font-medium text-foreground mt-6">{t('consentDuration')}</h3>
              <p>{t('consentDurationIntro')}</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>{t('consentDurationAccount')}</li>
                <li>{t('consentDurationRevoke')}</li>
              </ul>
              
              <h3 className="text-base font-medium text-foreground mt-6">{t('consentRights')}</h3>
              <p>{t('consentRightsIntro')}</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>{t('consentRightsAccess')}</li>
                <li>{t('consentRightsCorrect')}</li>
                <li>{t('consentRightsRestrict')}</li>
                <li>{t('consentRightsPortability')}</li>
                <li>{t('consentRightsComplaint')}</li>
              </ul>
              
              <p className="mt-6">{t('consentConclusion')}</p>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
